import type { UserRow } from '../userViewModel';

import InviteFormField from './InviteFormField';

export type EmailMatch =
  | { kind: 'backfill'; user: UserRow }
  | { kind: 'duplicate'; user: UserRow }
  | { kind: 'none' };

interface InviteEmailFieldProps {
  email: string;
  onChange: (value: string) => void;
  match: EmailMatch;
}

function accentText(match: EmailMatch, hasAt: boolean): string {
  if (match.kind === 'duplicate') return 'already exists';
  if (match.kind === 'backfill') return 'imported record match';
  return hasAt ? '✓ no Auth0 account' : '';
}

/** Border + background for the current match state. Each tint carries a
 *  dark-mode counterpart: the light tints are near-white, so without them the
 *  drawer's light dark-mode foreground would be typed onto a pale field. */
function stateClass(match: EmailMatch, hasAt: boolean): string {
  if (match.kind === 'duplicate') {
    return 'border-clay-600 bg-clay-50 dark:border-clay-500 dark:bg-[#33200f]';
  }
  if (match.kind === 'backfill') {
    return 'border-[#BA7517] bg-[#FAECD8] dark:border-[#BA7517] dark:bg-[#33260F]';
  }
  return hasAt
    ? 'border-moss-600 bg-moss-50 dark:border-moss-500 dark:bg-[#0d2b1c]'
    : 'border-stone-200 bg-white dark:border-stone-800 dark:bg-[#1a1f1c]';
}

/** Email input with imported-record / duplicate detection feedback. */
export default function InviteEmailField({ email, onChange, match }: InviteEmailFieldProps) {
  const hasAt = email.includes('@');
  return (
    <InviteFormField label="Work email" required accent={accentText(match, hasAt)}>
      <input
        type="email"
        value={email}
        onChange={(e) => onChange(e.target.value)}
        placeholder="someone@bonsaibar.com"
        className={`w-full h-9 px-2.5 rounded-lg border font-mono text-[12px] text-bark-900 dark:text-[#e8ece9] placeholder:text-stone-400 dark:placeholder:text-stone-500 outline-none focus:ring-2 focus:ring-moss-500 focus:ring-offset-1 ${stateClass(match, hasAt)}`}
      />
      {match.kind === 'duplicate' && (
        <div className="mt-1.5 text-[11.5px] text-clay-700 dark:text-clay-300">
          {match.user.name} is already in this tenant. Edit them from the list instead.
        </div>
      )}
    </InviteFormField>
  );
}
